import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Upload, Download, Database, Loader2, AlertTriangle, CheckCircle2, FileSpreadsheet, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Excel template column headers
const TEMPLATE_HEADERS = ['Date', 'Broker', 'Active Client', 'Lots', 'Commission USD', 'Deposit USD'];

export default function AdminDailyDataImportTab() {
  const [ibs, setIbs] = useState([]);
  const [selectedIb, setSelectedIb] = useState('');
  const [parsedRows, setParsedRows] = useState([]);
  const [fileName, setFileName] = useState('');
  const [importing, setImporting] = useState(false);
  const [lastBatch, setLastBatch] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/ib/list`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setIbs((res.data?.ibs || res.data || []).map(ib => ({ id: ib.id, name: ib.name }))))
      .catch(() => {});
  }, []);

  // Group rows by month and broker for preview summary
  const summary = useMemo(() => {
    const byMonth = {};
    parsedRows.forEach(r => {
      if (!r.date || !r.broker) return;
      const m = r.date.slice(0, 7);
      if (!byMonth[m]) byMonth[m] = {};
      const b = r.broker;
      if (!byMonth[m][b]) byMonth[m][b] = { rows: 0, lots: 0, commission_usd: 0, active: 0, deposit_usd: 0 };
      byMonth[m][b].rows += 1;
      byMonth[m][b].lots += Number(r.lots || 0);
      byMonth[m][b].commission_usd += Number(r.commission_usd || 0);
      byMonth[m][b].active += Number(r.active || 0);
      byMonth[m][b].deposit_usd += Number(r.deposit_usd || 0);
    });
    return byMonth;
  }, [parsedRows]);

  const downloadTemplate = () => {
    const ibName = ibs.find(x => x.id === selectedIb)?.name || 'IB';
    const ws = XLSX.utils.aoa_to_sheet([
      TEMPLATE_HEADERS,
      ['2026-03-01', 'octa',   5, 12.3,  145.50, 2000],
      ['2026-03-01', 'moneta', 3,  8.1,   95.20, 1500],
      ['2026-03-02', 'octa',   4, 10.5,  130.00, 1800],
      ['2026-03-02', 'moneta', 2,  5.2,   62.40, 1200],
    ]);
    ws['!cols'] = TEMPLATE_HEADERS.map(() => ({ wch: 18 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Daily Data');
    XLSX.writeFile(wb, `DailyData_Template_${ibName.replace(/\s/g, '_')}.xlsx`);
    toast.success('Template downloaded');
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { defval: '' });
      // Normalize headers (case-insensitive)
      const rows = json.map(row => {
        const lower = {};
        Object.entries(row).forEach(([k, v]) => { lower[k.toString().toLowerCase().trim()] = v; });
        // Handle date — accept YYYY-MM-DD string or Excel date serial number
        let date = lower['date'] || '';
        if (typeof date === 'number') {
          const d = XLSX.SSF.parse_date_code(date);
          if (d) {
            date = `${d.y}-${String(d.m).padStart(2, '0')}-${String(d.d).padStart(2, '0')}`;
          } else {
            date = '';
          }
        } else if (date instanceof Date) {
          const y = date.getFullYear(), m = date.getMonth() + 1, d = date.getDate();
          date = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        } else {
          date = String(date).trim();
        }
        return {
          date,
          broker: String(lower['broker'] || '').trim().toLowerCase(),
          active: Number(lower['active client'] ?? lower['active'] ?? 0) || 0,
          lots: Number(lower['lots'] || 0) || 0,
          commission_usd: Number(lower['commission usd'] ?? lower['commission'] ?? 0) || 0,
          deposit_usd: Number(lower['deposit usd'] ?? lower['deposit'] ?? 0) || 0,
        };
      }).filter(r => r.date && r.broker);
      setParsedRows(rows);
      toast.success(`Parsed ${rows.length} rows. Review preview before import.`);
    } catch (err) {
      toast.error('Failed to parse file: ' + (err.message || ''));
    }
    e.target.value = '';
  };

  const runImport = async () => {
    if (!selectedIb) { toast.error('Pilih IB dulu'); return; }
    if (parsedRows.length === 0) { toast.error('No rows parsed'); return; }
    const ibName = ibs.find(x => x.id === selectedIb)?.name || selectedIb;
    const monthList = Object.keys(summary).sort();
    if (!window.confirm(
      `Import ${parsedRows.length} rows to ${ibName}?\n\n` +
      `Months: ${monthList.join(', ')}\n\n` +
      `Existing broker entries with same date will be OVERWRITTEN.\n` +
      `Auto-backup saved (can undo via batch_id).`
    )) return;

    setImporting(true);
    setLastBatch(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/daily-data-import/${selectedIb}`, {
        rows: parsedRows,
      }, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
      setLastBatch(res.data);
      toast.success(`Import complete — ${res.data.inserted_entries + res.data.updated_entries} broker entries updated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    } finally { setImporting(false); }
  };

  const undoLast = async () => {
    if (!lastBatch?.batch_id) return;
    if (!window.confirm('Undo last import? This will restore the pre-import state.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/daily-data-import-undo/${lastBatch.batch_id}`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Import undone — original data restored');
      setLastBatch(null);
      setParsedRows([]);
      setFileName('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Undo failed');
    }
  };

  const fmt = (n) => (n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="p-5 rounded-xl bg-gradient-to-r from-[#10B981]/15 to-transparent border border-[#10B981]/30">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-xl bg-[#10B981]/20 border border-[#10B981]/40 flex items-center justify-center">
            <Upload size={22} className="text-[#10B981]" />
          </div>
          <div>
            <h3 className="text-base font-bold text-white">Daily Data Importer</h3>
            <p className="text-xs text-[hsl(var(--text-muted))]">Bulk upload broker sales data from Excel/CSV — repopulate daily_data_2026.sales[].brokers</p>
          </div>
        </div>
        <div className="flex items-start gap-2 mt-3 p-3 rounded-lg bg-[#3B82F6]/10 border border-[#3B82F6]/30">
          <Info size={14} className="text-[#3B82F6] mt-0.5 shrink-0" />
          <div className="text-[11px] text-[#93C5FD] space-y-1">
            <p><strong className="text-white">Use case:</strong> Recover lost broker data after cleanup OR bulk-enter historical broker data.</p>
            <p>1. Pilih IB → Download Template → Fill data per row (Date, Broker, Active, Lots, Commission USD, Deposit USD)</p>
            <p>2. Upload file → Review preview → Confirm Import</p>
            <p>3. <strong className="text-[#10B981]">Auto-backup ON</strong> — boleh undo dalam masa 90 hari</p>
          </div>
        </div>
      </div>

      {/* Step 1: Pick IB + Template */}
      <div className="p-4 rounded-xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))] flex items-center gap-3 flex-wrap">
        <label className="text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--text-muted))]">Target IB:</label>
        <Select value={selectedIb} onValueChange={setSelectedIb}>
          <SelectTrigger className="w-[260px] h-9 text-xs bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]" data-testid="import-ib-selector">
            <SelectValue placeholder="Pilih IB" />
          </SelectTrigger>
          <SelectContent>
            {ibs.map(ib => <SelectItem key={ib.id} value={ib.id}>{ib.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" onClick={downloadTemplate} disabled={!selectedIb} className="text-xs" data-testid="dl-template-btn">
          <Download size={12} className="mr-1.5" />Download Template
        </Button>
        <label className="ml-auto inline-flex items-center gap-2 cursor-pointer">
          <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFile} className="hidden" data-testid="import-file-input" />
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-[#10B981] hover:bg-[#059669] text-white">
            <FileSpreadsheet size={12} />Upload File
          </span>
        </label>
        {fileName && <span className="text-[11px] text-[hsl(var(--text-muted))]">{fileName} — {parsedRows.length} rows</span>}
      </div>

      {/* Preview */}
      {parsedRows.length > 0 && (
        <div className="p-4 rounded-xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))]">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h4 className="text-sm font-bold text-white">Preview Summary</h4>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => { setParsedRows([]); setFileName(''); }} className="text-xs">Clear</Button>
              <Button size="sm" onClick={runImport} disabled={importing || !selectedIb}
                className="text-xs bg-[#10B981] hover:bg-[#059669]" data-testid="confirm-import-btn">
                {importing ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Upload size={12} className="mr-1.5" />}
                Confirm Import
              </Button>
            </div>
          </div>

          {Object.keys(summary).sort().map(month => (
            <div key={month} className="mb-4 rounded-lg border border-[hsl(var(--border))] overflow-hidden">
              <div className="px-4 py-2 bg-[hsl(var(--surface-elevated))] border-b border-[hsl(var(--border))]">
                <span className="text-sm font-bold text-white">{month}</span>
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--surface))]">
                    <th className="px-3 py-2 text-left text-[10px] uppercase font-bold text-[hsl(var(--text-muted))]">Broker</th>
                    <th className="px-3 py-2 text-right text-[10px] uppercase font-bold text-[hsl(var(--text-muted))]">Days</th>
                    <th className="px-3 py-2 text-right text-[10px] uppercase font-bold text-[hsl(var(--text-muted))]">Active</th>
                    <th className="px-3 py-2 text-right text-[10px] uppercase font-bold text-[hsl(var(--text-muted))]">Lots</th>
                    <th className="px-3 py-2 text-right text-[10px] uppercase font-bold text-[hsl(var(--text-muted))]">Commission USD</th>
                    <th className="px-3 py-2 text-right text-[10px] uppercase font-bold text-[hsl(var(--text-muted))]">Deposit USD</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(summary[month]).map(([broker, t]) => (
                    <tr key={broker} className="border-b border-[hsl(var(--border))]/40">
                      <td className="px-3 py-2 font-semibold text-white">{broker}</td>
                      <td className="px-3 py-2 text-right font-mono">{t.rows}</td>
                      <td className="px-3 py-2 text-right font-mono">{t.active}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmt(t.lots)}</td>
                      <td className="px-3 py-2 text-right font-mono text-emerald-400">${fmt(t.commission_usd)}</td>
                      <td className="px-3 py-2 text-right font-mono text-blue-400">${fmt(t.deposit_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {/* Last import result */}
      {lastBatch && (
        <div className="p-4 rounded-xl bg-[#10B981]/10 border border-[#10B981]/30">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 size={16} className="text-[#10B981]" />
              <div>
                <p className="text-sm font-bold text-white">Import Complete</p>
                <p className="text-[11px] text-[#9CA3AF]">
                  {lastBatch.affected_months.length} months • {lastBatch.inserted_entries} new • {lastBatch.updated_entries} updated • {lastBatch.new_dates_added} new dates
                </p>
                <p className="text-[10px] text-[#9CA3AF]">Batch: <code>{lastBatch.batch_id}</code></p>
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={undoLast} className="text-xs border-[#E63946] text-[#E63946]">
              Undo this import
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
